import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

const TEAM = [
  { name:'Pratap Rana', role:'MD & Founder', bio:'Front-End Tech Lead with 8+ years of experience. Led the design, development, and delivery of 50+ digital products across e-commerce, SaaS, and healthcare, focusing on scalable user experiences, performance, and modern web technologies.', initials:'PR' },
  { name:'Milap Rana', role:'CEO & Co-Founder', bio:'Hybrid App Developer passionate about building high-performance mobile applications for both iOS and Android. Experienced in delivering scalable, user-focused solutions and contributing to successful product launches across multiple industries.', initials:'MR' },
  { name:'Arjun Verma', role:'Lead Developer', bio:'React and Node.js expert. Loves clean architecture and hates technical debt. Open-source contributor with 3k+ GitHub stars.', initials:'AV' },
  { name:'Priya Singh', role:'SEO & Marketing Lead', bio:'Digital marketing specialist with a track record of ranking competitive keywords and scaling paid ad campaigns profitably.', initials:'PS' },
  { name:'Amit Patel', role:'App Development Lead', bio:'React Native and Flutter developer. Has built apps with 100k+ downloads. Focused on performance and smooth UX.', initials:'AP' },
  { name:'Kavya Reddy', role:'Project Manager', bio:'Keeps every project on track. PMP certified with 6 years managing digital projects end-to-end.', initials:'KR' },
];

const VALUES = [
  { icon:'🎯', title:'Results First', desc:'We measure success by your growth — traffic, leads, revenue. Not vanity metrics.' },
  { icon:'💬', title:'Honest Communication', desc:'We tell you what you need to hear, not what you want to hear. No overpromising.' },
  { icon:'⚡', title:'Speed & Quality', desc:'We move fast without cutting corners. Quick delivery with no compromise on quality.' },
  { icon:'🔄', title:'Long-Term Thinking', desc:'We build things that last and scale. Not quick fixes that break in 6 months.' },
];

const STATS = [
  { value:'100+', label:'Projects Completed' },
  { value:'25+', label:'Happy Clients' },
  { value:'5+', label:'Years in Business' },
  { value:'10+', label:'Team Members' },
];

export default function About() {
  useScrollReveal();
  return (
    <>
      <SEO title="About Us — IT Services Company" description="CodeViora is an IT services company specializing in web development, web design, app development, SEO, and digital marketing. Meet the team behind 100+ successful projects." canonical="/about" />

      {/* Hero */}
      <section className="bg-grid-black pt-24 pb-20 md:pt-32 md:pb-28 relative overflow-hidden">
        <div className="container-max relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <span className="section-label-inv">About CodeViora</span>
              <h1 className="section-title-inv text-5xl md:text-6xl mt-2">WE BUILD THE INTERNET FOR BUSINESSES.</h1>
              <p className="section-sub-inv text-lg">CodeViora is an IT services company that helps businesses establish, grow, and dominate their digital presence.</p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/services" className="btn-primary-inv">Our Services</Link>
                <Link to="/contact" className="btn-outline-inv">Work With Us</Link>
              </div>
            </div>
            <div className="hidden lg:block animate-fade-in" style={{animationDelay:'0.2s'}}>
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80" alt="CodeViora team collaborating" className="rounded-2xl w-full h-80 object-cover border border-white/10" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-pure-black border-y border-white/10 py-10">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {STATS.map(({ value, label }) => (
              <div key={label} className="reveal">
                <p className="text-4xl text-white" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{value}</p>
                <p className="text-white/40 text-xs font-semibold mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80" alt="CodeViora office workspace" className="rounded-2xl w-full h-80 object-cover border border-black/10" loading="lazy" />
            </div>
            <div className="reveal" style={{transitionDelay:'100ms'}}>
              <span className="section-label">Our Story</span>
              <h2 className="section-title text-3xl md:text-4xl mt-1">FROM A SMALL TEAM TO A FULL IT STUDIO.</h2>
              <div className="flex flex-col gap-4 mt-6 text-black/60 text-sm leading-relaxed">
                <p>CodeViora started with a simple belief: every business deserves a strong digital presence, but most agencies make it too complicated, too expensive, or too slow. We set out to change that.</p>
                <p>We started as a two-person web development team. Today, we are a full-service IT company offering web development, app development, design, SEO, and digital marketing — with 100+ completed projects and 25+ satisfied clients.</p>
                <p>Our clients range from local businesses just getting online to funded startups scaling fast. What stays constant is our commitment: deliver real results, on time, with complete transparency.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">What We Do</span>
            <h2 className="section-title-inv">END-TO-END IT SERVICES.</h2>
            <p className="section-sub-inv mx-auto">Everything your business needs to succeed online, delivered by one dedicated team.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon:'🌐', title:'Web Development', desc:'Custom websites, web apps, WordPress, Shopify — built fast and built to last.' },
              { icon:'🎨', title:'Web Design', desc:'UI/UX design that converts visitors into customers. Premium, purposeful design.' },
              { icon:'📱', title:'App Development', desc:'Cross-platform mobile apps for iOS and Android using React Native and Flutter.' },
              { icon:'🔍', title:'SEO', desc:'Data-driven SEO strategies that improve rankings and drive consistent organic traffic.' },
              { icon:'📣', title:'Digital Marketing', desc:'Google Ads, Meta Ads, social media, and email marketing that delivers real ROI.' },
              { icon:'🛠️', title:'Maintenance & Support', desc:'Ongoing support, updates, and improvements to keep your digital products running.' },
            ].map(({ icon, title, desc }, i) => (
              <div key={title} className="reveal card-black p-7" style={{transitionDelay:`${i*60}ms`}}>
                <span className="text-3xl mb-4 block">{icon}</span>
                <h3 className="text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label">Our Values</span>
            <h2 className="section-title">HOW WE OPERATE.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {VALUES.map(({ icon, title, desc }, i) => (
              <div key={title} className="reveal card-white p-7 flex items-start gap-5" style={{transitionDelay:`${i*60}ms`}}>
                <span className="text-3xl flex-shrink-0">{icon}</span>
                <div>
                  <h3 className="font-bold text-black mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                  <p className="text-sm text-black/50 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">The Team</span>
            <h2 className="section-title-inv">THE PEOPLE DOING THE WORK.</h2>
            <p className="section-sub-inv mx-auto">No juniors handling your project after the sales call. The people you meet are the people who build.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEAM.map(({ name, role, bio, initials }, i) => (
              <div key={name} className="reveal card-black p-7" style={{transitionDelay:`${i*60}ms`}}>
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-black font-black text-lg mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>
                  {initials}
                </div>
                <h3 className="text-white mb-0.5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{name}</h3>
                <p className="text-xs text-white/40 font-semibold mb-3">{role}</p>
                <p className="text-sm text-white/40 leading-relaxed">{bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work images */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label">Our Work</span>
            <h2 className="section-title">SOME OF OUR PROJECTS.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { url:'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=600&q=80', alt:'Website project showcase' },
              { url:'https://images.unsplash.com/photo-1555421689-3f034debb7a6?w=600&q=80', alt:'Mobile app project' },
              { url:'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80', alt:'Digital marketing campaign' },
              { url:'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&q=80', alt:'SEO analytics dashboard' },
              { url:'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=600&q=80', alt:'UI design project' },
              { url:'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&q=80', alt:'Web development project' },
            ].map(({ url, alt }, i) => (
              <div key={i} className="reveal overflow-hidden rounded-2xl border border-black/10 group" style={{transitionDelay:`${i*60}ms`}}>
                <img src={url} alt={alt} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-pure-black py-20 border-t border-white/10">
        <div className="container-max text-center reveal">
          <h2 className="text-4xl md:text-5xl text-white mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>READY TO WORK TOGETHER?</h2>
          <p className="text-white/40 text-lg mb-8 max-w-lg mx-auto">We are currently accepting new clients. Let's talk about what we can build for you.</p>
          <Link to="/contact" className="btn-primary-inv">
            Start a Project
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M3 9H15M11 5l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </Link>
        </div>
      </section>
    </>
  );
}
